import { ReactElement, useState, useMemo, memo } from 'react';
import { CircularProgress } from '@mui/material';
import { find } from 'lodash';

import { Nullable } from '~globals/types/commons';
import { DataPaginateResponse } from '~globals/types/api';
import { useRequest } from '~hooks/useRequest';
import { getWarehouses } from '~services/warehouse';
import { WarehouseExtended, WarehouseHead } from '~services/warehouse/types';
import { getItemByIds } from '~services/item';
import { ItemExtended } from '~services/item/types';
import { VehicleSearchItem } from '~services/vehicle/types';
import { DriverCompanyBase } from '~services/driver/types';
import { getAdminCompanySetting } from '~services/admin/companySetting';
import { AdminCompanySetting } from '~services/admin/companySetting/types';
import { DEFAULT_UUIID_EMPTY } from '~constants/commons';

import {
  AssistantGenerateRoutesDialogContainer,
  AssistantGenerateRoutesDialogLoadingContainer,
} from '../styles';

import { AssistantGenerateRoutesDialogByItemsProps } from './types';
import AssistantGenerateRoutesDialogByItemsProvider from './Context';
import AssistantGenerateRoutesDialogByItemsContent from './Content';

const AssistantGenerateRoutesDialogByItems = ({
  open,
  onClose,
  actions,
  itemIds,
}: AssistantGenerateRoutesDialogByItemsProps): ReactElement => {
  const [warehouses, setWarehouses] = useState<WarehouseExtended[]>([]);
  const [lastSelectedWarehouse, setLastSelectedWarehouse] =
    useState<Nullable<WarehouseExtended>>(null);
  const [selectedDriver, setSelectedDriver] =
    useState<Nullable<DriverCompanyBase>>(null);

  const [, loadingGetWarehouses] = useRequest({
    request: getWarehouses,
    payload: null,
    withPostSuccess: (response) => {
      const {
        data: { results },
        head: { lastWarehouseId },
      } = response.data as DataPaginateResponse<
        WarehouseExtended[],
        null,
        WarehouseHead
      >;

      setWarehouses(results);

      let lastSelectedWarehouseData = results[0];

      if (lastWarehouseId && lastWarehouseId !== DEFAULT_UUIID_EMPTY) {
        const lastWarehouseData = find(results, {
          warehouseId: lastWarehouseId,
        });

        if (lastWarehouseData) {
          lastSelectedWarehouseData = lastWarehouseData;
        }
      }

      setLastSelectedWarehouse(lastSelectedWarehouseData);
    },
  });

  const [items, setItems] = useState<ItemExtended[]>([]);

  const [, loadingGetItems] = useRequest({
    initialLoading: false,
    request: getItemByIds,
    payload: itemIds,
    withPostSuccess: (response) => {
      const currentItems = response.data?.data.results as ItemExtended[];

      setItems(currentItems);
    },
  });

  const [vehicles, setVehicles] = useState<VehicleSearchItem[]>([]);

  const [isFeedbackRequestAudit, setIsFeedbackRequestAudit] = useState(false);

  const [, loadingGetCompanySettings] = useRequest({
    request: getAdminCompanySetting,
    payload: null,
    withPostSuccess: (response) => {
      const companySettings = response.data?.data as AdminCompanySetting;

      setIsFeedbackRequestAudit(Boolean(companySettings.feedbackRequestAudit));
    },
  });

  const loading = useMemo(
    () => loadingGetWarehouses || loadingGetItems || loadingGetCompanySettings,
    [loadingGetWarehouses, loadingGetItems, loadingGetCompanySettings],
  );

  return (
    <AssistantGenerateRoutesDialogContainer open={open}>
      {loading && (
        <AssistantGenerateRoutesDialogLoadingContainer>
          <CircularProgress color="primary" disableShrink size={80} />
        </AssistantGenerateRoutesDialogLoadingContainer>
      )}

      {!loading && (
        <AssistantGenerateRoutesDialogByItemsProvider
          value={{
            onClose,
            data: {
              items,
              warehouses: { list: warehouses, lastSelectedWarehouse },
              vehicles: { value: vehicles, setValue: setVehicles },
              selectedDriver: {
                value: selectedDriver,
                setValue: setSelectedDriver,
              },
              isFeedbackRequestAudit,
            },
            actions,
          }}
        >
          <AssistantGenerateRoutesDialogByItemsContent />
        </AssistantGenerateRoutesDialogByItemsProvider>
      )}
    </AssistantGenerateRoutesDialogContainer>
  );
};

export default memo(AssistantGenerateRoutesDialogByItems);
